import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@auctisync/ui';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SignOutButton } from '../../components/SignOutButton';
import { readClaims, useSession } from '../../hooks/useSession';
import { fetchWithAuth } from '../../lib/accessToken';

/**
 * Competitor research — Feature 4 dashboard. Cross-tenant operational
 * view, super_admin only. Hits IE Worker at `/v1/dashboard/competitors`.
 */

type WindowKey = '1d' | '7d' | '30d';
const WINDOWS: readonly { key: WindowKey; label: string }[] = [
  { key: '1d', label: 'Last 1d' },
  { key: '7d', label: 'Last 7d' },
  { key: '30d', label: 'Last 30d' },
];

interface ChangeBlock {
  kind: 'added' | 'removed';
  excerpt: string;
  containsPricingSignal: boolean;
}
interface CompetitorDiff {
  url: string;
  competitor: string;
  segment: string;
  snapshotDate: string;
  priorSnapshotDate: string | null;
  newHash: string;
  priorHash: string | null;
  paragraphsAdded: number;
  paragraphsRemoved: number;
  changes: ChangeBlock[];
}
interface CompetitorResearchSnapshot {
  windowDays: number;
  builtAtMs: number;
  totalDiffs: number;
  diffs: CompetitorDiff[];
}
interface ApiError {
  error: string;
  detail: string;
}

type FetchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; data: CompetitorResearchSnapshot }
  | { kind: 'error'; status: number; message: string };

function ieBaseUrl(): string | null {
  const url = import.meta.env.VITE_INTELLIGENCE_BASE_URL as string | undefined;
  return url && url.length > 0 ? url.replace(/\/$/, '') : null;
}

export function SuperCompetitorResearch() {
  const { session, userId } = useSession();
  const { appRole } = readClaims(session);
  const [windowKey, setWindowKey] = useState<WindowKey>('7d');
  const [state, setState] = useState<FetchState>({ kind: 'idle' });
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const base = ieBaseUrl();
    if (!base) {
      setState({
        kind: 'error',
        status: 0,
        message: 'VITE_INTELLIGENCE_BASE_URL is not configured.',
      });
      return;
    }
    if (userId === null) {
      setState({ kind: 'error', status: 0, message: 'No active session.' });
      return;
    }
    setState({ kind: 'loading' });
    const url = `${base}/v1/dashboard/competitors?window=${windowKey}&limit=100`;
    fetchWithAuth(url)
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ApiError | null;
          setState({
            kind: 'error',
            status: res.status,
            message: body?.detail ?? `HTTP ${res.status}`,
          });
          return;
        }
        const data = (await res.json()) as CompetitorResearchSnapshot;
        setState({ kind: 'ok', data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'unknown error';
        setState({ kind: 'error', status: 0, message });
      });
    return () => {
      cancelled = true;
    };
  }, [windowKey, refreshNonce, userId]);

  return (
    <main style={{ padding: '2rem', maxWidth: '76rem', margin: '0 auto' }}>
      <div
        style={{
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.875rem',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '0.25rem',
            }}
          >
            Competitor research
          </h1>
          <p style={{ color: 'var(--text-muted)' }}>
            Signed in as {session?.user.email ?? 'unknown'} ·{' '}
            <Badge variant="outline">{appRole ?? 'no role'}</Badge> ·{' '}
            <Link to="/super/dashboard" style={{ color: 'var(--brand-primary)' }}>
              ← Super dashboard
            </Link>
          </p>
        </div>
        <SignOutButton redirectTo="/super" />
      </div>

      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
        }}
      >
        {WINDOWS.map((w) => (
          <button
            key={w.key}
            onClick={() => setWindowKey(w.key)}
            type="button"
            style={{
              padding: '0.4rem 0.9rem',
              borderRadius: '0.4rem',
              border: '1px solid var(--border)',
              background: windowKey === w.key ? 'var(--brand-primary)' : 'var(--surface-elev)',
              color: windowKey === w.key ? 'var(--brand-primary-fg)' : 'var(--text)',
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            {w.label}
          </button>
        ))}
        <button
          onClick={() => setRefreshNonce((n) => n + 1)}
          type="button"
          disabled={state.kind === 'loading'}
          style={{
            padding: '0.4rem 0.9rem',
            borderRadius: '0.4rem',
            border: '1px solid var(--border)',
            background: 'var(--surface-elev)',
            color: 'var(--text)',
            fontWeight: 600,
            fontSize: '0.85rem',
            cursor: state.kind === 'loading' ? 'wait' : 'pointer',
            marginLeft: 'auto',
          }}
        >
          {state.kind === 'loading' ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {state.kind === 'idle' || state.kind === 'loading' ? (
        <Card>
          <CardContent>
            <p style={{ color: 'var(--text-muted)' }}>
              {state.kind === 'idle' ? 'Initializing…' : 'Loading diffs…'}
            </p>
          </CardContent>
        </Card>
      ) : state.kind === 'error' ? (
        <Card>
          <CardHeader>
            <CardTitle>Diffs unavailable</CardTitle>
            <CardDescription>HTTP {state.status || 'no-network'}</CardDescription>
          </CardHeader>
          <CardContent>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                color: 'var(--semantic-danger)',
                fontFamily: 'var(--font-mono, ui-monospace)',
                fontSize: '0.85rem',
                margin: 0,
              }}
            >
              {state.message}
            </pre>
          </CardContent>
        </Card>
      ) : (
        <DiffsView data={state.data} />
      )}
    </main>
  );
}

function DiffsView({ data }: { data: CompetitorResearchSnapshot }) {
  if (data.diffs.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No diffs in window</CardTitle>
          <CardDescription>
            Last {data.windowDays} day{data.windowDays === 1 ? '' : 's'} produced 0 diffs. Either
            the daily scrape hasn't run yet, every URL is unchanged, or every site disallowed our
            bot in robots.txt.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {data.diffs.map((d) => (
        <DiffCard key={`${d.url}::${d.snapshotDate}`} diff={d} />
      ))}
    </div>
  );
}

function DiffCard({ diff }: { diff: CompetitorDiff }) {
  const totalChanges = diff.paragraphsAdded + diff.paragraphsRemoved;
  const hasPricing = diff.changes.some((c) => c.containsPricingSignal);
  return (
    <Card>
      <CardHeader>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', flexWrap: 'wrap' }}>
          <CardTitle>
            {diff.competitor}
            {hasPricing && (
              <span
                style={{
                  marginLeft: '0.6rem',
                  fontSize: '0.7rem',
                  color: 'var(--semantic-warning)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                pricing signal
              </span>
            )}
          </CardTitle>
        </div>
        <CardDescription>
          <a
            href={diff.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--text-muted)', wordBreak: 'break-all' }}
          >
            {diff.url}
          </a>
          {' · '}
          {diff.snapshotDate}
          {diff.priorSnapshotDate !== null && ` (vs ${diff.priorSnapshotDate})`}
          {' · '}+{diff.paragraphsAdded} / −{diff.paragraphsRemoved} paragraphs
          {' · '}
          {totalChanges} total
        </CardDescription>
      </CardHeader>
      <CardContent>
        {diff.changes.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            No ranked changes persisted (capped at 50 — counts above are the full delta).
          </p>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {diff.changes.map((c, i) => (
              <ChangeRow key={i} change={c} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ChangeRow({ change }: { change: ChangeBlock }) {
  const color = change.kind === 'added' ? 'var(--semantic-success)' : 'var(--semantic-danger)';
  const sign = change.kind === 'added' ? '+' : '−';
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: '0.5rem',
        padding: '0.4rem 0',
        borderBottom: '1px solid var(--border-subtle, var(--border))',
        fontSize: '0.85rem',
      }}
    >
      <span
        style={{
          color,
          fontWeight: 700,
          fontFamily: 'var(--font-mono, ui-monospace)',
        }}
      >
        {sign}
      </span>
      <div>
        <span style={{ color: 'var(--text)' }}>{change.excerpt}</span>
        {change.containsPricingSignal && (
          <span
            style={{
              marginLeft: '0.5rem',
              fontSize: '0.7rem',
              color: 'var(--semantic-warning)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            pricing
          </span>
        )}
      </div>
    </li>
  );
}
